/*
  Las Ruanas — motion engine.

  Three independent units, each guarded so a missing target or a thrown
  constructor never stops the next one: the scroll reveal, the historia
  parallax, and the add-to-cart toast.

  What this file deliberately does NOT do:
  - it never writes opacity/transform/transition on .lr-reveal elements. The
    whole visual contract lives in lr-tokens.css, gated behind .lr-motion-ready.
  - it never computes a stagger. Every delay is already declarative, either as
    an inline transition-delay or as --lr-reveal-delay on the markup. A second
    source of delay would double it.
  - it never adds .lr-motion-ready. That has to happen before first paint, so
    it lives in the inline <head> script in theme.liquid; by the time this
    deferred file runs the hidden state is already applied.
*/
(function () {
  var FAILSAFE_MS = 2200;
  var TOAST_MS = 2600;

  // Shared so the reveal's MutationObserver can ignore the toast's own DOM
  // writes instead of rescanning the page because of them.
  var toastNode = null;

  var reduceMotion = false;
  try {
    reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (error) {
    reduceMotion = false;
  }

  /* Scroll reveal --------------------------------------------------------

     The only job is adding .lr-revealed. Everything below exists so that no
     failure mode can leave content stuck at opacity 0: if the observer is
     missing, throws, or simply never fires, the failsafe reveals the whole
     current set at 2.2s. Elements appended later (the collection grid's
     "Cargar más piezas") are picked up by a MutationObserver and get their own
     2.2s failsafe, so a late batch is covered by the same guarantee. */

  function revealAll(scope) {
    var nodes = (scope || document).querySelectorAll('.lr-reveal:not(.lr-revealed)');
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('lr-revealed');
  }

  function initReveal() {
    if (reduceMotion) {
      // The CSS already neutralises the hidden state, but marking the elements
      // keeps the DOM consistent for anything reading the class.
      revealAll();
      return;
    }

    var observer = null;
    try {
      observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            entries[i].target.classList.add('lr-revealed');
            observer.unobserve(entries[i].target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    } catch (error) {
      observer = null;
    }

    if (!observer) {
      revealAll();
      return;
    }

    // Failsafe queue: the nodes waiting for their 2.2s deadline, plus the single
    // timer that drains them. One timer for any number of rescans, so a churny
    // page cannot accumulate pending timeouts — the deadline of the oldest
    // queued batch still governs, so nothing waits longer than before.
    var queued = [];
    var failsafeTimer = null;

    function scheduleFailsafe(nodes) {
      // Most rescans on a settled page match nothing; arming a timer to drain
      // an empty queue is pure waste.
      if (!nodes.length) return;
      for (var i = 0; i < nodes.length; i++) queued.push(nodes[i]);
      if (failsafeTimer) return;
      failsafeTimer = window.setTimeout(function () {
        failsafeTimer = null;
        var batch = queued;
        queued = [];
        for (var i = 0; i < batch.length; i++) batch[i].classList.add('lr-revealed');
      }, FAILSAFE_MS);
    }

    function observeIn(scope) {
      // Snapshot the batch: the failsafe must reveal these nodes only, never
      // whatever the whole document happens to hold 2.2s later.
      var found = (scope || document).querySelectorAll('.lr-reveal:not(.lr-revealed)');
      var nodes = [];
      for (var i = 0; i < found.length; i++) {
        nodes.push(found[i]);
        observer.observe(found[i]);
      }
      scheduleFailsafe(nodes);
    }

    observeIn(document);

    if (!window.MutationObserver) return;
    var pendingScan = false;
    new MutationObserver(function (records) {
      if (pendingScan) return;
      // The toast lives in the observed subtree and writes to itself on every
      // add-to-cart; its mutations can never add a .lr-reveal.
      var relevant = false;
      for (var i = 0; i < records.length; i++) {
        var target = records[i].target;
        if (toastNode && target && toastNode.contains(target)) continue;
        relevant = true;
        break;
      }
      if (!relevant) return;
      pendingScan = true;
      // One scan per frame: filtering added nodes rules out grandchildren, and
      // the grid appends a whole subtree at once.
      window.requestAnimationFrame(function () {
        pendingScan = false;
        observeIn(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Parallax -------------------------------------------------------------

     The image is 118% tall inside an overflow-hidden wrapper, so it can slide
     between -8% and -16% without ever exposing an edge. -8% is the resting
     value already in CSS; scrolling past drifts it to -16%. */

  function initParallax() {
    if (reduceMotion) return;

    var image = document.querySelector('[data-lr-parallax]');
    if (!image) return;
    var frame = image.closest ? image.closest('.lr-historia__media') : null;
    if (!frame) return;

    var pending = false;

    function update() {
      pending = false;
      var rect = frame.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      // Off-screen costs nothing: no write, no layout thrash.
      if (rect.bottom <= 0 || rect.top >= viewport) return;

      // 0 when the frame's top sits at the bottom of the viewport, 1 when its
      // bottom has reached the top.
      var progress = (viewport - rect.top) / (viewport + rect.height);
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      image.style.transform = 'translate3d(0, ' + (-8 - progress * 8).toFixed(2) + '%, 0)';
    }

    function schedule() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    // Resize fires in bursts too (window drag, mobile URL bar): same rAF gate,
    // so the read/write pair still runs at most once per frame.
    window.addEventListener('resize', schedule, { passive: true });
    update();
  }

  /* Toast ----------------------------------------------------------------

     Add-to-cart confirmation. It subscribes to Dawn's cart-update event rather
     than patching product-form.js, and it is purely additive: <cart-notification>
     stays in the DOM, upgraded, and keeps re-rendering the cart sections. Only
     its visual presentation is suppressed, in CSS.

     One reused node, so repeated adds replace the message instead of stacking.
     role="status" + aria-live="polite" announces it without moving focus. */

  function initToast() {
    var hideTimer = null;

    // Built and attached empty at init, never on first add: a live region is
    // only announced when it was already in the accessibility tree before the
    // text changed. Empty at rest, so this attachment announces nothing.
    toastNode = document.createElement('div');
    toastNode.className = 'lr-toast';
    toastNode.setAttribute('role', 'status');
    toastNode.setAttribute('aria-live', 'polite');
    toastNode.setAttribute('data-lr-toast', '');
    var text = document.createElement('p');
    text.className = 'lr-toast__text';
    toastNode.appendChild(text);
    document.body.appendChild(toastNode);

    function show(message) {
      var host = toastNode;
      host.querySelector('.lr-toast__text').textContent = message;
      // Reduced motion still gets the message: CSS drops the transition, the
      // class toggle and the 2.6s dismissal are identical.
      host.classList.add('lr-toast--visible');
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(function () {
        host.classList.remove('lr-toast--visible');
        // Empty it too. The hidden toast is only transparent, so a leftover
        // string stays reachable to a virtual cursor as text with nothing
        // visible behind it. Clearing after the class keeps the live region
        // itself in place for the next add.
        host.querySelector('.lr-toast__text').textContent = '';
      }, TOAST_MS);
    }

    // Exposed so other scripts can reuse the toast without importing anything.
    window.lrToast = show;

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'product-form') return;
      show('Agregado al carrito');
    });
  }

  function start() {
    try {
      initReveal();
    } catch (error) {
      revealAll();
    }
    try {
      initParallax();
    } catch (error) {
      /* Decoration only: a parallax failure must not stop the toast. */
    }
    try {
      initToast();
    } catch (error) {
      /* The cart still works without visible confirmation. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
